import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { TomeButton, TomePanel, TomeTitleBlock } from '../../primitives/tome';
import { zoneTomeVibe } from '../../primitives/tome/zoneTomeVibes';
import { Z } from '../../theme';
import { keyBindingsNavigationStore } from '../../Windows/KeyBindings/keyBindingsNavigationStore';
import { settingsNavigationStore } from '../../Windows/Settings/settingsNavigationStore';
import { isGuestSession, leaveWorld, type WorldExit } from '../../../services/worldExit';
import { gameMenuStore, useGameMenuOpen } from './gameMenuStore';
import './gameMenu.css';

function currentZoneId(): string | null {
  return (globalThis as { nm?: { getRoom?: () => { state?: { mapId?: string } } | null } })
    .nm?.getRoom?.()?.state?.mapId ?? null;
}

const LEAVING_LABEL: Record<WorldExit, string> = {
  characterSelect: 'Saving your hero…',
  logOut: 'Signing out…',
};

export interface GameMenuProps {
  /** The hero's name, the header's title (D39). */
  heroName?: string;
  /** The zone's display name, under the hero's name (D39). */
  zoneName?: string;
}

/**
 * The game menu (stream note 47): Esc with nothing else to close opens it.
 * A small tome page in the zone's vibe: Resume, Settings (the existing
 * settings window), Character select (leave the room, land on the roster) and
 * Log out (sign out, land on the title). A guest has no roster to return to
 * (D27), so a guest sees "End guest session" in place of both exits.
 * The header names the hero and the zone (D39); without a hero it reads
 * "Game Menu".
 */
export const GameMenu: React.FC<GameMenuProps> = ({ heroName, zoneName }) => {
  const open = useGameMenuOpen();
  const [leaving, setLeaving] = useState<WorldExit | null>(null);
  if (!open) return null;

  const guest = isGuestSession();
  const resume = () => { if (!leaving) gameMenuStore.close(); };
  const settings = () => { if (!leaving) settingsNavigationStore.openFromGameMenu(); };
  const keyBindings = () => { if (!leaving) keyBindingsNavigationStore.openFromGameMenu(); };
  const exit = (to: WorldExit) => {
    if (leaving) return;
    setLeaving(to);
    void leaveWorld(to);
  };

  // Portalled to the app root (like NPCWindowLayer): above the HUD's stacking context.
  return createPortal(<div className="game-menu-layer" style={{ zIndex: Z.modal }} data-testid="game-menu-layer">
    <div className="game-menu-backdrop" aria-hidden="true" onClick={resume} />
    <TomePanel className="game-menu" role="dialog" aria-modal="true" aria-label="Game menu"
      shapeSeed="game-menu" vibe={zoneTomeVibe(currentZoneId())}
      header={<TomeTitleBlock eyebrow="Game Menu"
        title={heroName?.trim() ? <span title={heroName.trim()}>{heroName.trim()}</span> : 'Game Menu'}
        aside={zoneName?.trim() ? <span className="game-menu-zone" data-testid="game-menu-zone">{zoneName.trim()}</span> : undefined}
        onClose={resume} closeLabel="Resume game" />}>
      <nav className="game-menu-actions" aria-label="Game menu choices" aria-busy={leaving ? true : undefined}>
        <TomeButton tone="primary" fullWidth disabled={!!leaving} onClick={resume}>Resume</TomeButton>
        <TomeButton tone="secondary" fullWidth disabled={!!leaving} data-testid="game-menu-settings" onClick={settings}>Settings</TomeButton>
        <TomeButton tone="secondary" fullWidth disabled={!!leaving} data-testid="game-menu-key-bindings" onClick={keyBindings}>
          Key Bindings
        </TomeButton>
        {!guest && <TomeButton tone="secondary" fullWidth disabled={!!leaving} onClick={() => exit('characterSelect')}>
          Character select
        </TomeButton>}
        <TomeButton tone="secondary" fullWidth disabled={!!leaving} onClick={() => exit('logOut')}>
          {guest ? 'End guest session' : 'Log out'}
        </TomeButton>
      </nav>
      {guest && !leaving && <p className="game-menu-note">
        Guest heroes are not saved. Sign in with Twitch to keep your heroes and choose between them.
      </p>}
      {leaving && <p className="game-menu-note" role="status">{LEAVING_LABEL[leaving]}</p>}
    </TomePanel>
  </div>, document.getElementById('root') ?? document.body);
};
